// planCatalog.jsx — what each plan SAYS, in one place, plus the columns that
// draw it.
//
// Braeden 2026-09-06, on the usage meter's "Get more usage" button: "it needs
// to pop up the normal both options to choose from, like the plans, dont we
// have a pop up for that already"
//
// We did, and it was welded shut. The card layout he means is the signup
// wizard's plan step (AccountMenu's `step === 'plan'`): three columns, the
// `.plan-fullscreen` / `.plan-cols` / `.pcol` markup. But it is a STEP — it is
// only reachable after form → confirm, its Continue calls `choosePlan()` which
// records signup consent and finalises a pending session, and the modal that
// hosts it refuses to open at all for someone already signed in
// (`if (state === 'in') return`). None of that fits a person who already has
// an account and just wants more usage.
//
// So the popup is opened standalone (PlanPopup.jsx) and the CARDS live here,
// used by both surfaces. The alternative was a second hand-written copy of the
// same three cards, and this repo has already paid for that lesson twice —
// ariaAboutUs went into one file because four copies of a guard drifted, and
// the crew aside in firstRunFlow drifted the same way. Prices and promises are
// the last thing that should be written down twice.

import React from 'react';

/**
 * A perk is a plain string, or { strong, text } when part of it is bold —
 * matching what the signup columns already render.
 */
export const PLAN_CARDS = {
  free: {
    key: 'free',
    name: 'Free',
    price: '$0',
    priceSuffix: null,
    tag: '$5 of Aria a week · up to $1.50/day',
    perks: [
      'Answers + internet search',
      '4 quick images a day',
      'Aria guides, you drive',
    ],
    note: 'Your conversations may be used to train and improve Aria.',
  },
  pro: {
    key: 'pro',
    name: 'HowTo Pro',
    price: '$20',
    priceSuffix: '/mo',
    badge: 'MOST POPULAR',
    tag: 'Everything on, nothing trains on you',
    perks: [
      { strong: 'Private', text: ' — never used for training' },
      'Unlimited conversations + voice',
      'Images, video, all workbenches',
      'Aria can take the controls',
    ],
    note: 'We keep only what the law requires — billing & security records.',
  },
  ultra: {
    key: 'ultra',
    name: 'HowTo Ultra',
    price: '$200',
    priceSuffix: '/mo',
    badge: 'ULTRA · API',
    badgeUltra: true,
    tag: 'Put Aria inside your own software',
    perks: [
      { strong: 'Aria API', text: ' — build Aria into your app, site, or tools' },
      'Full developer access + keys & docs',
      '$200/mo base + pay-as-you-go API usage',
      'Everything in Pro, private',
    ],
    note: 'For businesses & developers embedding Aria. API usage billed on top.',
  },
};

export const PLAN_PRICE_LABEL = { free: '$0', pro: '$20/mo', ultra: '$200/mo' };

function Perk({ perk }) {
  if (perk && typeof perk === 'object') {
    return <li><strong>{perk.strong}</strong>{perk.text}</li>;
  }
  return <li>{perk}</li>;
}

/**
 * The selectable plan columns. `keys` controls which and in what order, so the
 * signup wizard can show all three while the upgrade popup shows the two
 * Braeden put on the menu (2026-09-04: "there needs to eb two options right
 * now, a free one and one for 20$ a month thats IT" — creditsService.getPlans
 * returns exactly those two).
 */
export function PlanColumns({ keys = ['free', 'pro', 'ultra'], selected, onSelect, currentKey = null }) {
  const cards = keys.map((k) => PLAN_CARDS[k]).filter(Boolean);
  return (
    <div className={`plan-cols${cards.length === 2 ? ' plan-cols--two' : ''}`}>
      {cards.map((p) => (
        <button
          key={p.key}
          type="button"
          className={`pcol${p.key === 'pro' ? ' pcol--pro' : ''}${p.key === 'ultra' ? ' pcol--ultra' : ''}${selected === p.key ? ' pcol--on' : ''}`}
          onClick={() => onSelect && onSelect(p.key)}
          aria-pressed={selected === p.key}
        >
          {p.badge && (
            <div className={`pcol__badge${p.badgeUltra ? ' pcol__badge--ultra' : ''}`}>{p.badge}</div>
          )}
          <div className="pcol__name">{p.name}</div>
          <div className="pcol__price">
            {p.price}{p.priceSuffix ? <span>{p.priceSuffix}</span> : null}
          </div>
          <div className="pcol__tag">{p.tag}</div>
          <ul className="pcol__perks">
            {p.perks.map((perk, i) => <Perk key={i} perk={perk} />)}
          </ul>
          <p className="pcol__note">{p.note}</p>
          {currentKey === p.key && <div className="pcol__current">You&apos;re on this now</div>}
        </button>
      ))}
    </div>
  );
}

export default PlanColumns;
